import { HttpClient, HttpEvent, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Headers, Http, RequestMethod, RequestOptions, Response, ResponseContentType } from '@angular/http';

import { Observable } from 'rxjs/Rx';

import { CBMError, Credentials } from 'app/models';
import { StorageKey } from 'app/models/enums';

import { OAuthService } from './oauth.service';

@Injectable()
export class HttpService {
  private static readonly HTTP_STATUS_CODE_UNAUTHORIZED = 401;

  constructor(
    private readonly http: Http,
    private readonly httpClient: HttpClient,
    private readonly oauthService: OAuthService
  ) {}

  get<T>(url: string, headers?: Headers): Observable<T> {
    const options: RequestOptions = this.options(headers);

    return this.http
      .get(url, options)
      .map(this.tryJson)
      .catch(errors =>
        this.retryAfterRefresh(RequestMethod.Get, errors, url, null, options)
          .map(this.tryJson)
          .catch(this.handleErrorObservable)
      );
  }

  getFile<T>(url: string, headersGetFile?: Headers): Observable<T> {
    const options: RequestOptions = this.optionsGetFile(headersGetFile);

    return this.http
      .get(url, options)
      .catch(errors =>
        this.retryAfterRefresh(RequestMethod.Get, errors, url, null, options).catch(this.handleErrorObservable)
      );
  }

  post(url: string, body: any, headers?: Headers): Observable<any> {
    const options: RequestOptions = this.options(headers);

    return this.http
      .post(url, body, options)
      .catch(errors =>
        this.retryAfterRefresh(RequestMethod.Post, errors, url, body, options).catch(this.handleErrorObservable)
      );
  }

  /**
   * Similar to post, but ALWAYS refresh token before attempting the post.
   * Used for file uploads that take a long time.
   */
  postFile(url: string, body: any, headers?: Headers): Observable<any> {
    const options: RequestOptions = this.options(headers);
    const error = { status: HttpService.HTTP_STATUS_CODE_UNAUTHORIZED }; // force a refresh
    return this.retryAfterRefresh(RequestMethod.Post, error, url, body, options).catch(this.handleErrorObservable);
  }

  postFileWithProgress<T>(url: string, body: any): Observable<HttpEvent<T> | {}> {
    return this.oauthService.performTokenRefresh().switchMap((tokens: any) => {
      let headers = new HttpHeaders();
      headers = headers.append('Authorization', `Bearer ${tokens}`);

      const req = new HttpRequest('POST', url, body, {
        headers,
        reportProgress: true
      });

      return this.httpClient.request(req).catch(this.handleErrorObservable);
    });
  }

  put(url: string, body: any, headers?: Headers): Observable<any> {
    const options: RequestOptions = this.options(headers);

    return this.http
      .put(url, body, options)
      .catch(errors =>
        this.retryAfterRefresh(RequestMethod.Put, errors, url, body, options).catch(this.handleErrorObservable)
      );
  }

  delete(url: string, headers?: Headers): Observable<any> {
    const options: RequestOptions = this.options(headers);

    return this.http
      .delete(url, options)
      .catch(errors =>
        this.retryAfterRefresh(RequestMethod.Delete, errors, url, null, options).catch(this.handleErrorObservable)
      );
  }

  headersWithCredentials(): Headers {
    const headers = this.headersWithoutCredentials();
    headers.set('Authorization', `Bearer ${this.getCredentials().accessToken}`);
    return headers;
  }

  headersWithoutCredentials(): Headers {
    return new Headers({ 'Content-Type': 'application/json' });
  }

  headersWithCredentialsWithoutContentType(): Headers {
    const headers = new Headers();
    headers.set('Authorization', `Bearer ${this.getCredentials().accessToken}`);
    return headers;
  }

  options(headers?: Headers): RequestOptions {
    if (headers == null) {
      headers = this.headersWithCredentials();
    }
    return new RequestOptions({ headers });
  }

  private tryJson<T>(res: Response): T | Response {
    try {
      return res.json() as T;
    } catch (e) {
      return res;
    }
  }

  private optionsGetFile(headersGetFile?: Headers): RequestOptions {
    if (headersGetFile == null) {
      headersGetFile = this.headersWithCredentialsWithoutContentType();
    }

    return new RequestOptions({ headers: headersGetFile, responseType: ResponseContentType.ArrayBuffer });
  }

  private getCredentials(): Credentials {
    return JSON.parse(localStorage.getItem(StorageKey.CREDENTIALS));
  }

  private retryAfterRefresh(
    method: RequestMethod,
    err: { status: number },
    url: string,
    body: any,
    options: RequestOptions
  ): Observable<Response> {
    if (err.status === HttpService.HTTP_STATUS_CODE_UNAUTHORIZED) {
      try {
        return this.oauthService.performTokenRefresh().switchMap(accessToken => {
          options.headers.set('Authorization', `Bearer ${accessToken}`);
          options.method = method;
          options.body = body;
          return this.http.request(url, options);
        });
      } catch (e) {
        return Observable.throw(err);
      }
    } else {
      return Observable.throw(err);
    }
  }

  private handleErrorObservable(error: Response | any): Observable<any> {
    console.error('handle error: ', error);
    let errMsgs: CBMError[];
    if (typeof error.status !== 'undefined') {
      if (error.status === 0) {
        errMsgs = [new CBMError(error.status, '')];
      } else {
        try {
          // NOTE: Calling .json() on an object with a _body field seems to just return its _body!
          const json = error.json();
          if (Array.isArray(json)) {
            errMsgs = error.json() as CBMError[];
          } else {
            errMsgs = [new CBMError(error.status, error._body)];
          }
        } catch (e) {
          // NOTE: _body is likely undefined here
          errMsgs = [new CBMError(error.status, error._body)];
        }
      }
    } else {
      // something went wrong, although it's not really a 500 status
      errMsgs = [new CBMError('500', '')];
    }
    return Observable.throw(errMsgs);
  }
}



// WEBPACK FOOTER //
// ./src/app/services/http.service.ts